import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@ndd/ui";
import React, {
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import type { AppointmentManualGenerationErrorCode } from "../../../../shared/api-types";
import type {
  AppointmentManualGenerationInput,
  AppointmentManualGenerationResult,
} from "@/lib/appointment-documentation-state";

/**
 * NO-775: what the dialog says for each closed refusal. The pasted text is
 * never echoed back; the RD's own text stays in the boxes to try again.
 */
const PASTE_TRANSCRIPT_ERROR_COPY: Record<
  AppointmentManualGenerationErrorCode,
  string
> = {
  invalid_request: "Paste the visit transcript to start.",
  field_too_large: "That text is too long. Each box holds up to 512 KB.",
  manual_generation_unavailable:
    "Generating from a pasted transcript isn't available right now.",
  transcript_exists:
    "This visit already has a transcript from Healthie. Close this and use Enhance notes.",
  appointment_identity_incomplete:
    "This appointment is missing its Healthie appointment or patient, so the draft couldn't be sent back to Healthie. Nothing was saved.",
  appointment_identity_ambiguous:
    "This patient has more than one Healthie record right now. Nothing was saved. Try again once the records are merged.",
  appointment_identity_unresolved:
    "This appointment isn't fully linked to Healthie yet. Nothing was saved.",
  session_conflict:
    "This visit's transcript couldn't be attached, so generation didn't start.",
  generation_not_started:
    "The draft didn't start. If the transcript was saved, Enhance notes can start it.",
};

export function pasteTranscriptErrorMessage(
  code: AppointmentManualGenerationErrorCode
): string {
  return PASTE_TRANSCRIPT_ERROR_COPY[code];
}

/**
 * The Paste transcript dialog: a required transcript, an optional prior SOAP
 * note, and one action. The claim key and the typed text live as long as the
 * workspace does, so a retry after a lost response replays the same request
 * instead of starting a second one, and closing the dialog keeps the text.
 */
export function PasteTranscriptDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    input: AppointmentManualGenerationInput
  ) => Promise<AppointmentManualGenerationResult>;
}): ReactElement {
  const form = usePasteTranscriptForm(onSubmit, () => onOpenChange(false));

  return (
    <Dialog open={open} onOpenChange={(next) => !form.sending && onOpenChange(next)}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Paste transcript</DialogTitle>
          <DialogDescription>
            Healthie has no transcript for this visit. Paste it here to build the draft.
            A prior SOAP note is optional; without one, the last signed note in Healthie
            is used.
          </DialogDescription>
        </DialogHeader>
        <PasteTranscriptForm {...form} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * NO-775: the Paste transcript action, offered only while
 * `manual-soap-generation` is on for the signed-in RD. Off, the footer gets no
 * handler and no dialog mounts. The workspace passes the evaluated flag.
 */
export function usePasteTranscriptAction(
  generate: (
    input: AppointmentManualGenerationInput
  ) => Promise<AppointmentManualGenerationResult>,
  enabled: boolean
): { dialog: ReactNode; footerProps: { onPasteTranscript?: () => void } } {
  const [open, setOpen] = useState(false);

  if (!enabled) return { dialog: null, footerProps: {} };

  return {
    dialog: (
      <PasteTranscriptDialog open={open} onOpenChange={setOpen} onSubmit={generate} />
    ),
    footerProps: { onPasteTranscript: () => setOpen(true) },
  };
}

function usePasteTranscriptForm(
  onSubmit: (
    input: AppointmentManualGenerationInput
  ) => Promise<AppointmentManualGenerationResult>,
  onStarted: () => void
) {
  const [claimKey] = useState(() => crypto.randomUUID());
  const [transcript, setTranscript] = useState("");
  const [priorNote, setPriorNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (sending) return;

    if (transcript.trim() === "") {
      setError(pasteTranscriptErrorMessage("invalid_request"));

      return;
    }

    setSending(true);
    setError(null);
    const result = await onSubmit({
      claimKey,
      transcript,
      ...(priorNote.trim() === "" ? {} : { priorNote }),
    });
    setSending(false);

    if (result.status === "started") {
      onStarted();

      return;
    }

    setError(pasteTranscriptErrorMessage(result.code));
  }

  return {
    transcript,
    priorNote,
    sending,
    error,
    onTranscriptChange: setTranscript,
    onPriorNoteChange: setPriorNote,
    onSubmit: () => void submit(),
  };
}

export interface PasteTranscriptFormProps {
  transcript: string;
  priorNote: string;
  sending: boolean;
  error: string | null;
  onTranscriptChange: (value: string) => void;
  onPriorNoteChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

/** The dialog body, separate so it renders without a portal in tests. */
export function PasteTranscriptForm({
  transcript,
  priorNote,
  sending,
  error,
  onTranscriptChange,
  onPriorNoteChange,
  onSubmit,
  onCancel,
}: PasteTranscriptFormProps): ReactElement {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="paste-transcript-text">Transcript</Label>
        <Textarea
          id="paste-transcript-text"
          value={transcript}
          required
          disabled={sending}
          rows={10}
          onChange={(event) => onTranscriptChange(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="paste-transcript-prior-note">
          Prior SOAP note{" "}
          <span className="font-normal text-muted-foreground">(optional)</span>
        </Label>
        <Textarea
          id="paste-transcript-prior-note"
          value={priorNote}
          disabled={sending}
          rows={6}
          onChange={(event) => onPriorNoteChange(event.target.value)}
        />
      </div>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="ghost" disabled={sending} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={sending || transcript.trim() === ""}>
          {sending ? "Starting…" : "Generate draft"}
        </Button>
      </DialogFooter>
    </form>
  );
}
